import EditRecordItem, { EditItem } from '@/component/EditRecordItemV2';
import SpeakerAvatar from '@/pages/Voice/SpearkerAvatar';
import { ReactNode, useState } from 'react';
import { IPG } from '@/types/summary';
import Popover from '@/component/PopoverRc';
import CollaborationSpeakerPopover from './CollaborationSpeakerPopover';
import AsrHandler from '@/bussiness/AsrHandler';
import fridayReporter from '@/utils/friday';
import { IEditRecordParams, IMergeRecordParams } from '@/types/record';

export interface IProps {
  speakerName: string;
  useDefaultIcon?: boolean;
  avatarIndex?: number;
  recordTime?: string;
  content?: ReactNode;
  speaking?: boolean;
  avatar?: ReactNode;
  unstableContent?: string;
  unstableTranslateContent?: string;
  record?: IPG;
  pi?: string; // 段落 ID，用于编辑操作
  editable?: boolean; // 是否可编辑
  isTrans?: boolean; // 是否是译文
  onEditRecord?: (params: IEditRecordParams) => Promise<void>; // 流名称
  onMergeRecord?: (params: IMergeRecordParams) => void;
  onModifySpeakerName?: (updatedParas: IPG[], editRoleType: 'ONE' | 'BATCH') => Promise<void>; // 修改发言人
  onSpeakerPopoverChange?: (visible: boolean) => void; // 发言人弹窗状态变化回调
}

const ContentItem = ({
  speakerName,
  useDefaultIcon,
  recordTime,
  avatarIndex,
  content,
  speaking = false,
  avatar,
  unstableContent,
  record,
  pi,
  editable = true,
  isTrans,
  onEditRecord,
  onModifySpeakerName,
  onSpeakerPopoverChange,
  onMergeRecord,
}: IProps) => {

  const [speakerPopoverVisible, setSpeakerPopoverVisible] = useState(false);

  const handleEdit = (pi: string, editItems: EditItem[], emptyWordAllowed = false) => {
    console.log("handleEdit", editItems, emptyWordAllowed);

    AsrHandler.editRecord(pi, editItems, (params) => {
      onEditRecord?.(params);
    });
  };

  // 处理失焦回调
  const handleBlur = (blurPara: { pi: string; pContent: string }) => {
    console.log("ContentItem handleBlur", blurPara);
    AsrHandler.correctRecordOnBlur(blurPara);
  };

  // 处理单个发言人修改
  const handleEditSingle = (newSpeakerName: string) => {
    if (!pi) {
      console.warn('ContentItem: pi is required for speaker editing');
      return;
    }

    const handleSingleSpeakerModify = async (updatedParas: IPG[]) => {
      onModifySpeakerName?.(updatedParas, 'ONE');
    };

    AsrHandler.modifySpeakerName(pi, [newSpeakerName], handleSingleSpeakerModify);
    setSpeakerPopoverVisible(false);

    fridayReporter.pushData({
      eventCode: "CollaborationPage_Speaker_EditSingle",
    });
  };

  // 处理全局发言人修改
  const handleEditAll = (newSpeakerName: string, oldSpeakerName: string) => {
    if (!pi) {
      console.warn('ContentItem: pi is required for speaker editing');
      return;
    }

    const handleBatchSpeakerModify = async (updatedParas: IPG) => {
      onModifySpeakerName?.([updatedParas], 'BATCH');
    };
    AsrHandler.modifyAllSameSpeakerName(pi, [oldSpeakerName], [newSpeakerName], handleBatchSpeakerModify);
    setSpeakerPopoverVisible(false);

    fridayReporter.pushData({
      eventCode: "CollaborationPage_Speaker_EditAll",
    });
  };

  // 更新发言人弹窗状态并通知父组件
  const updateSpeakerPopoverVisible = (visible: boolean) => {
    setSpeakerPopoverVisible(visible);
    onSpeakerPopoverChange?.(visible);
  };

  const handleMerge = (pi: string) => {
    AsrHandler.mergeRecord(pi, (params) => {
      onMergeRecord?.(params);
    });
  };

  // const handleSplit = (pi: string, si: string, cIndex: number) => {
  //   // 在分割前记录当前光标位置
  //   onCursorPositionChange?.({ id: pi, pos: 0 });
  //   AsrHandler.splitRecord(pi, si, cIndex);
  // };

  return (
    <div className={`text-3 ${speaking ? 'flex items-start' : ''}`}>
      <div className={`flex items-center text-3 relative ${!speaking ? 'pb-1' : ''}`}>
        {avatar ? avatar
          : (
            <SpeakerAvatar
              speakerName={speakerName}
              avatarIndex={avatarIndex}
              useDefaultAvatarIcon={useDefaultIcon}
              className="size-5 mr-2"
              fontSize="0.5rem"
            />
          )}
        {speakerName && (
          <Popover
            destroyTooltipOnHide
            hover={false}
            placement="bottomLeft"
            onVisibleChange={(vis) => {
              if (!editable || speaking) return;
              updateSpeakerPopoverVisible(vis);
              if (vis) {
                fridayReporter.pushData({
                  eventCode: "CollaborationPage_Speaker_Click",
                });
              }
            }}
            className="pt-0"
            visible={speakerPopoverVisible}
            content={
              <CollaborationSpeakerPopover
                originValue={speakerName}
                onEditSingle={handleEditSingle}
                onEditAll={handleEditAll}
              />
            }
          >
            <div
              className={`relative mr-1 text-mx-tertiary ${editable && !speaking && 'cursor-pointer'
                } text-3`}
            >
              {speaking ? '' : isTrans ? speakerName.replace('发言人', 'Speaker') : speakerName}
            </div>
          </Popover>
        )}
        <div className="text-mx-quaternary">{recordTime}</div>
      </div>
      <div
        className={`${speaking ? 'text-3.5 lh-5' : 'text-3.5 lh-7'
          } ${speaking ? isTrans ? 'text-#2D63CF/60' : 'text-mx-secondary/50' : ''}`}
      >
        {record && pi ? (
          <EditRecordItem
            record={record}
            isTrans={isTrans}
            editable={editable}
            handleEdit={(_, editItems, emptyWordAllowed) => handleEdit(pi, editItems, emptyWordAllowed)}
            handleMerge={handleMerge}
            // handleEnter={(editItem, cIndex, pi) => handleSplit(pi, editItem.si, cIndex)}
            onBlur={handleBlur}
            className={isTrans ? 'text-#2D63CF!' : 'text-mx-secondary!'}
          />
        ) : (
          content
        )}
        {speaking && <span>{unstableContent || '请说话，我会把语音转成文字...'}</span>}
      </div>
    </div>
  );
};

export default ContentItem;